import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Star, MapPin, ArrowLeft, Search, Check, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { ServiceRequestService } from '@/services/serviceRequestService';
import { ServiceRequest, JobProposal } from '@/types/serviceRequest';
import { decodeHashData, getCurrentUrlHash } from '@/lib/hashUtils';
import { FadeIn } from '@/components/Animations';
import { supabase } from '@/integrations/supabase/client';
import { BusinessBadges } from '@/components/BusinessBadges';
import { useBusinessesByIds } from '@/hooks/useBusinessesByIds';
import { ReachOutModal } from '@/components/ReachOutModal';
import { PATH } from '@/routes/routeConfig';
import Layout from '@/components/Layout';

const ReviewPotentialMatch: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  
  const [searchData, setSearchData] = useState<any>(null);
  const [businessIds, setBusinessIds] = useState<string[]>([]);
  const [selectedBusinessIds, setSelectedBusinessIds] = useState<Set<string>>(new Set());
  const [isReachOutModalOpen, setIsReachOutModalOpen] = useState(false);

  // Fetch business details using React Query
  const { data: businessMatches = [], isLoading: isLoadingBusinesses, error: businessError } = useBusinessesByIds({
    businessIds,
    enabled: businessIds.length > 0
  });

  useEffect(() => {
    const loadSearchResults = () => {
      try {
        // Decode search results from URL hash
        const hash = getCurrentUrlHash();
        const data = decodeHashData(hash);
        
        if (!data || !data.searchParams) {
          console.error('No search data found in URL hash');
          toast({
            title: "Error",
            description: "No search results found. Please start a new search.",
            variant: "destructive"
          });
          navigate(PATH.SERVICE_REQUEST);
          return;
        }
        
        setSearchData(data);
        setBusinessIds(data.businessIds || []);
        
      } catch (error) {
        console.error('Error loading search results:', error);
        toast({
          title: "Error",
          description: "Failed to load search results. Please start a new search.",
          variant: "destructive"
        });
        navigate(PATH.SERVICE_REQUEST);
      }
    };

    loadSearchResults();
  }, [navigate, toast]);

  // Re-load data if businessIds is empty but hash data exists (handles auth recovery)
  useEffect(() => {
    if (businessIds.length === 0) {
      const hash = getCurrentUrlHash();
      const data = decodeHashData(hash);
      if (data?.businessIds?.length > 0) {
        console.log('Restoring business IDs after auth recovery:', data.businessIds);
        setBusinessIds(data.businessIds);
        setSearchData(data);
      }
    }
  }, [businessIds.length]);

  // Multi-select helper functions
  const toggleBusinessSelection = (businessId: string) => {
    console.log('Toggling selection for businessId:', businessId);
    setSelectedBusinessIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(businessId)) {
        newSet.delete(businessId);
      } else {
        newSet.add(businessId);
      }
      console.log('Updated selectedBusinessIds:', Array.from(newSet));
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedBusinessIds.size === businessMatches.length) {
      // If all selected, clear selection
      setSelectedBusinessIds(new Set());
    } else {
      // Select all
      const allBusinessIds = new Set(businessMatches.map(business => business.id));
      setSelectedBusinessIds(allBusinessIds);
    }
  };

  const isBusinessSelected = (businessId: string) => selectedBusinessIds.has(businessId);
  const isAllSelected = selectedBusinessIds.size === businessMatches.length && businessMatches.length > 0;
  const isSomeSelected = selectedBusinessIds.size > 0 && selectedBusinessIds.size < businessMatches.length;

  const handleReachOutToSelectedBusinesses = () => {
    if (selectedBusinessIds.size === 0) {
      toast({
        title: "Error",
        description: "Please select at least one business to reach out to.",
        variant: "destructive"
      });
      return;
    }

    if (!searchData?.searchParams) {
      toast({
        title: "Error",
        description: "Search parameters not found. Please start a new search.",
        variant: "destructive"
      });
      return;
    }

    setIsReachOutModalOpen(true);
  };

  const handleReachOutSuccess = (result: { serviceRequestId: string; conversationId?: string }) => {
    // Navigate to My Service Requests page with the specific service request
    if (result.serviceRequestId) {
      navigate(`${PATH.MY_SERVICE_REQUESTS}/${result.serviceRequestId}`);
    } else {
      // Fallback to My Service Requests list
      navigate(PATH.MY_SERVICE_REQUESTS);
    }
  };


  if (isLoadingBusinesses || !searchData) {
    return (
      <Layout>
        <div className="container mx-auto py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">{t('review_match.loading.matches')}</p>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (businessError) {
    return (
      <Layout>
        <div className="container mx-auto py-8 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl font-bold mb-4">Error loading business details</h1>
            <Button onClick={() => navigate(PATH.SERVICE_REQUEST)}>Start New Search</Button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="container mx-auto pt-6 pb-4 px-4 bg-white"
      >
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <FadeIn direction="up" delay={0.1}>
            <div className="mb-6">
              <div className="flex items-center gap-4 mb-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    const currentHash = getCurrentUrlHash();
                    navigate(`${PATH.SERVICE_REQUEST}${currentHash ? `#${currentHash}` : ''}`);
                  }}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  {t('review_match.button.previous')}
                </Button>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">
                {businessMatches.length > 0 
                  ? t('review_match.page.title')
                  : t('review_match.no_results.title')
                }
              </h1>
              <p className="text-gray-600 text-sm md:text-base">
                {businessMatches.length > 0 ? (
                  <>
                    {businessMatches.length === 1 
                      ? t('review_match.page.description.single', { count: businessMatches.length })
                      : t('review_match.page.description.multiple', { count: businessMatches.length })
                    }
                    <br />
                    {t('review_match.page.instruction')}
                  </>
                ) : (
                  t('review_match.no_results.description')
                )}
              </p>
              
              {/* Multi-select controls */}
              {businessMatches.length > 0 && (
                <div className="flex items-center justify-between mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={toggleSelectAll}
                      className="w-5 h-5"
                    />
                    <label className="text-sm font-medium cursor-pointer" onClick={toggleSelectAll}>
                      Select All ({businessMatches.length})
                    </label>
                  </div>
                  
                  {selectedBusinessIds.size > 0 && (
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={handleReachOutToSelectedBusinesses}
                        className="bg-primary hover:bg-primary/90 text-white px-4 py-2"
                      >
                        <ArrowRightLeft className="w-4 h-4 mr-2" />
                        {t('review_match.button.reach_out')} ({selectedBusinessIds.size})
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </FadeIn>


          {/* Business Matches */}
          <div className="space-y-4">
            {businessMatches.map((business, index) => (
              <FadeIn key={business.id} direction="up" delay={0.2 + index * 0.1}>
                <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 overflow-hidden bg-white">
                  {/* Banner Image Section */}
                  <div className="relative aspect-[3/1] md:aspect-[4/2.5] w-full">
                    <img
                      src={business.company_banner_url || business.company_logo || `https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3`}
                      alt={business.name || 'Business'}
                      className="w-full h-full object-cover"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/20" />
                    
                    {/* Selection Checkbox - Top Left */}
                    <div className="absolute top-3 left-3">
                      <div 
                        className={`w-6 h-6 rounded-md border-2 cursor-pointer transition-all duration-200 flex items-center justify-center ${
                          isBusinessSelected(business.id)
                            ? 'bg-primary border-primary shadow-lg' 
                            : 'bg-white/90 border-white shadow-md hover:bg-white'
                        }`}
                        onClick={() => toggleBusinessSelection(business.id)}
                      >
                        {isBusinessSelected(business.id) && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                    </div>
                    
                    {/* Available Badge - Top Right */}
                    <div className="absolute top-3 right-3">
                      <Badge className="bg-green-500 text-white shadow-md text-xs">
                        {t('review_match.status.available')}
                      </Badge>
                    </div>
                  </div>

                  <CardContent className="p-4">
                    {/* Business Header */}
                    <div className="mb-3">
                      <h3 className="text-lg font-bold text-gray-900 mb-1">
                        {business.name || 'Service Provider'}
                      </h3>
                      
                      {/* Rating and Reviews */}
                      {business.reviews && business.reviews.length > 0 ? (
                        <div className="flex items-center gap-1 mb-2">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <span className="text-sm font-medium text-gray-700">
                            {(business.reviews.reduce((sum: number, review: any) => sum + review.rating, 0) / business.reviews.length).toFixed(1)}
                          </span>
                          <span className="text-sm text-gray-500">
                            {business.reviews.length === 1 
                              ? t('review_match.reviews.count.single', { count: business.reviews.length })
                              : t('review_match.reviews.count.multiple', { count: business.reviews.length })
                            }
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 mb-2">
                          <Star className="w-4 h-4 text-gray-300" />
                          <span className="text-sm text-gray-500">{t('review_match.status.no_reviews')}</span>
                        </div>
                      )}

                      {/* Location */}
                      {(business.city || business.postal_code) && (
                        <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
                          <MapPin className="w-3.5 h-3.5" />
                          <span>
                            {[business.city, business.postal_code].filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Business Badges */}
                    <div className="mb-4">
                      <BusinessBadges 
                        businessId={business.id} 
                        mode="compact" 
                        maxBadges={2}
                        showSkeletonOnLoad={false}
                      />
                    </div>


                    {/* Selection Status */}
                    <div className="text-center py-2">
                      {isBusinessSelected(business.id) && (
                        <div className="flex items-center justify-center gap-2 text-primary font-medium">
                          <Check className="w-4 h-4" />
                          <span className="text-sm">{t('review_match.status.selected')}</span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </FadeIn>
            ))}
          </div>

          {businessMatches.length === 0 && (
            <FadeIn direction="up" delay={0.2}>
              <Card className="text-center py-8 border-0 shadow-lg">
                <CardContent>
                  <p className="text-gray-600 text-sm mb-6">
                    {t('review_match.no_results.options')}
                  </p>
                  <Button 
                    onClick={() => navigate(PATH.SERVICE_REQUEST)}
                    className="min-h-[44px] px-6 bg-primary hover:bg-primary/90 text-white"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    {t('review_match.no_results.refine_button')}
                  </Button>
                </CardContent>
              </Card>
            </FadeIn>
          )}
        </div>

        {/* Reach Out Modal */}
        <ReachOutModal
          isOpen={isReachOutModalOpen}
          onClose={() => setIsReachOutModalOpen(false)}
          selectedBusinesses={businessMatches.filter(business => selectedBusinessIds.has(business.id))}
          searchParams={searchData?.searchParams}
          onSuccess={handleReachOutSuccess}
        />
      </motion.div>
    </Layout>
  );
};

export default ReviewPotentialMatch;